import React, { useState, forwardRef, useImperativeHandle } from 'react';
import {
  Dropdown,
  DatePicker,
  DatePickerInput,
  TextArea,
  NumberInput,
  RadioButton,
  RadioButtonGroup,
} from '@carbon/react';

const prescribedDrugOptions = [
  { id: 'ofatumumab', text: 'ケシンプタ（オファツムマブ）' },
  { id: 'siponimod',  text: 'メーゼント（シポニモド）' },
  { id: 'dmf',        text: 'テクフィデラ（フマル酸ジメチル）' },
  { id: 'natalizumab',text: 'タイサブリ（ナタリズマブ）' },
  { id: 'fingolimod', text: 'イムセラ・ジレニア（フィンゴリモド）' },
  { id: 'glatiramer', text: 'コバキソン（グラチラマー酢酸塩）' },
  { id: 'ifn_beta1a', text: 'アボネックス（IFNβ-1a）' },
  { id: 'ifn_beta1b', text: 'ベタフェロン（IFNβ-1b）' },
  { id: 'none',       text: '無治療' },
];

// ▼ ダミー「前回」
const dummyPrevious = {
  edssScore: 2.5,
  prescribedDrug: { id: 'none', text: '無治療' },
  cognitive: { date: '2025-07-10', method: { id: 'cogeval', text: 'CogEVAL' }, result: '25/30' },
  walking:   { date: '2025-07-15', t25fwSec: 6.8 },
  arm:       { date: '2025-07-18', domSec: 19.2, nonDomSec: 21.4 },
};

const MRI_OPTIONS = [
  { id: 'mri-yes',  value: '1',  label: 'あり' },
  { id: 'mri-no',   value: '0',  label: 'なし' },
  { id: 'mri-na',   value: 'na', label: '未実施' },
];

const toDateArray = (iso) => (iso ? [new Date(iso)] : []);
const todayIso = new Date().toISOString();

// ---- EDSS用ユーティリティ ----
const clamp = (v, min, max) => Math.min(max, Math.max(min, v));
const roundToHalf = (v) => Math.round(v * 2) / 2; // 0.5刻み
const safeNumber = (v, fallback = 0) => {
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : fallback;
};
const formatOneDecimal = (v) =>
  v === '' || v == null ? '' : safeNumber(v).toFixed(1);

function ConsultationFormInner({ onSubmit, patientId = 'P010' }, ref) {
  const [formValues, setFormValues] = useState({
    visitDate: todayIso,
    edssScore: dummyPrevious.edssScore, // 数値で保持
    prescribedDrug: dummyPrevious.prescribedDrug,
    hasMriLesion: null,
    hasRelapse: null,
    doctorComment: '',
  });

  // 今後拡張しやすいように exams は別管理
  const [exams] = useState({
    cognitive: { date: dummyPrevious.cognitive.date, method: dummyPrevious.cognitive.method, result: dummyPrevious.cognitive.result },
    walking:   { date: dummyPrevious.walking.date,   t25fwSec: dummyPrevious.walking.t25fwSec },
    arm:       { date: dummyPrevious.arm.date,       domSec: dummyPrevious.arm.domSec, nonDomSec: dummyPrevious.arm.nonDomSec },
  });

  const handleTopChange = (k, v) => setFormValues((p) => ({ ...p, [k]: v }));

  // 親へ公開する submit()
  useImperativeHandle(ref, () => ({
    submit: () => {
      const payload = {
        ...formValues,
        // 念のため数値で保証
        edssScore: safeNumber(formValues.edssScore),
        results: { ...exams },
        patientId,
      };
      onSubmit?.(payload);
    },
  }));

  // レイアウト系
  const rowWrap = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '1.5rem',
    borderBottom: '1px solid #e0e0e0',
  };
  const col = { flex: '1 1 30%', minWidth: '260px' };
  const subtle = { fontSize: '0.8rem', color: '#6f6f6f', marginTop: '0.25rem' };
  const twoColWrap = { display: 'flex', flexWrap: 'wrap', gap: '1.5rem' };
  const twoCol = { flex: '1 1 45%', minWidth: '280px' };

  return (
    <div style={{ maxWidth: 1000 }}>
      <div style={{ ...rowWrap, borderBottom: 'none', marginBottom: 0, paddingBottom: 0 }}>
        <div style={{ ...col, maxWidth: 400 }}>
          <h4>診察日</h4>
          <DatePicker
            dateFormat="Y/m/d"
            datePickerType="single"
            value={toDateArray(formValues.visitDate)}
            onChange={(dates) => handleTopChange('visitDate', dates[0]?.toISOString() ?? '')}
          >
            <DatePickerInput id="visit-date" labelText="" placeholder="年/月/日" />
          </DatePicker>
        </div>
      </div>

      <div style={twoColWrap}>
        <div style={twoCol}>
          <h4>EDSSスコア</h4>
          <NumberInput
            id="edssScore"
            label="EDSSスコア"
            hideLabel
            min={0}
            max={10}
            step={0.5}
            // 表示は常に 1 桁
            value={formatOneDecimal(formValues.edssScore)}
            onChange={(_, data) => {
              // Carbonのdata.valueは文字列のことがあるので安全に処理
              const raw = safeNumber(data.value, formValues.edssScore ?? 0);
              const rounded = roundToHalf(raw);
              const clamped = clamp(rounded, 0, 10);
              handleTopChange('edssScore', clamped);
            }}
            size="md"
            style={{ marginBottom: 0 }}
          />
          <div style={subtle}>前回：{formatOneDecimal(dummyPrevious.edssScore)}</div>
        </div>

        <div style={twoCol}>
          <h4>処方薬</h4>
          <Dropdown
            id="prescribedDrug"
            label="処方薬を選択"
            items={prescribedDrugOptions}
            itemToString={(item) => item?.text ?? ''}
            selectedItem={formValues.prescribedDrug}
            onChange={({ selectedItem }) => handleTopChange('prescribedDrug', selectedItem)}
            size="md"
            style={{ marginBottom: 0 }}
          />
          <div style={subtle}>前回：{dummyPrevious.prescribedDrug?.text ?? '—'}</div>
        </div>

        <div style={twoCol}>
          <h4>MRIに病変</h4>
          <RadioButtonGroup
            name="hasMriLesion"
            orientation="vertical"
            valueSelected={formValues.hasMriLesion ?? ''}
            onChange={(val) => handleTopChange('hasMriLesion', val)}
            style={{ marginTop: 0 }}
          >
            {MRI_OPTIONS.map((opt) => (
              <RadioButton key={opt.id} id={opt.id} value={opt.value} labelText={opt.label} />
            ))}
          </RadioButtonGroup>
        </div>

        <div style={twoCol}>
          <h4>再発の有無</h4>
          <RadioButtonGroup
            name="hasRelapse"
            orientation="vertical"
            valueSelected={formValues.hasRelapse ?? ''}
            onChange={(val) => handleTopChange('hasRelapse', val)}
            style={{ marginTop: 0 }}
          >
            <RadioButton labelText="あり" value="1" id="relapse-yes" />
            <RadioButton labelText="なし" value="0" id="relapse-no" />
          </RadioButtonGroup>
        </div>
      </div>

      <div style={{ ...rowWrap, borderBottom: 'none' }}>
        <div style={{ ...col, minWidth: '100%', maxWidth: '100%' }}>
          <h4>医師コメント</h4>
          <TextArea
            id="doctorComment"
            labelText=""
            placeholder="コメントを入力してください"
            value={formValues.doctorComment}
            onChange={(e) => handleTopChange('doctorComment', e.target.value)}
            rows={4}
          />
        </div>
      </div>
    </div>
  );
}

export default forwardRef(ConsultationFormInner);
